/**
 * Entry recovery for a configurator session that arrives ALREADY PAID.
 *
 * Every session can now come back from `POST /session/load` with an `invoice_id`, and that invoice
 * can already be `paid` while the session itself is still `configuration` — the backend does not
 * advance a session when the money lands, it waits to be told
 * (`POST /session/payment-acknowledged`). Verified live on test1 (2026-07-30): session
 * `b0d57c78…` sat at `configuration` with a `paid` Stripe invoice attached.
 *
 * A flow that ignores this drops the buyer back into a wizard they have already paid for, and
 * every write from there fails: onboarding's first `/session/save` is rejected with
 * `INVALID_SESSION_STATE`, and the brand can never be deployed. So on entry each flow asks the
 * two questions this hook answers, once per session:
 *
 *   1. is there an invoice, and is it paid? → `GET /session/invoice` (`sessionFetchInvoiceStatus`).
 *      A READ, deliberately: `POST /session/invoice` supersedes the invoice, which on the SPEI
 *      rail voids bank coordinates the buyer may already be transferring to.
 *   2. if so, and the session has not moved on, acknowledge it → the backend's `can_continue`
 *      verdict, which is what the confirmation screen needs to choose between "check your inbox"
 *      and "Continue to Owner Studio".
 *
 * Shared by all four flows (`v0`, `v3`, `v0-mx`, `v3-mx`) because the situation is rail-agnostic:
 * a card charge confirmed by webhook after the buyer closed the tab and a SPEI transfer that
 * cleared overnight arrive in exactly the same state.
 *
 * All state lives in the SESSION SLICE, not in this hook. That is deliberate: a flow mounts this
 * in its root (to decide where to send the owner) AND in its checkout step (to render the right
 * screen), and per-instance state would let those two disagree — the first instance would consume
 * the one-shot guard and the second would report "nothing to resume" for a session the buyer had
 * just paid for.
 */
import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  sessionAcknowledgePayment,
  sessionFetchInvoiceStatus,
  selectNeedsPaymentAcknowledgement,
  selectSessionCheckedOut,
  selectShowPaymentConfirmation,
} from '@/store/slices/session';
import type { PaymentAcknowledgement } from '@/utils/session-payment';

export type { PaymentAcknowledgement };

export interface ResumePaidSession {
  /**
   * The backend's verdict, once the payment has been acknowledged. `null` means it did not apply
   * (no invoice, not paid, session already advanced elsewhere) or the call gave no answer — either
   * way the confirmation screen falls back to its own signals rather than showing nothing.
   */
  acknowledgement: PaymentAcknowledgement | null;
  /**
   * This session's checkout is already DONE, so the flow should show its confirmation screen
   * rather than an order summary the buyer can no longer change. Stays true across the
   * acknowledge transition.
   */
  checkedOut: boolean;
  /**
   * THIS visit acknowledged the payment, so it owes the buyer the confirmation screen. A flow root
   * redirects on it: the acknowledge advances the session to `customization`, which trips the
   * onboarding guard and would otherwise bounce the just-paid buyer to Studio Home before they
   * ever see their confirmation.
   *
   * Narrower than `checkedOut`, which also covers an owner who paid on an earlier visit and has
   * since moved into onboarding — they must keep being sent to their onboarding.
   */
  showConfirmation: boolean;
  /**
   * The entry probe / acknowledge is still resolving, so `checkedOut` is not yet trustworthy.
   * A checkout that would otherwise start itself on mount must wait for this to clear, or it
   * fires a payment attempt against an invoice that is already paid.
   */
  resolving: boolean;
}

/**
 * Probe the loaded session's invoice and, when it is paid but the session has not been advanced,
 * acknowledge the payment exactly once.
 *
 * Safe to call from anywhere in a flow, and safe to call more than once per flow: it is inert
 * until the session has loaded, it never fires for an unpaid or absent invoice, and the once-guard
 * is shared through the store (and latched in `localStorage`, so a reload cannot re-fire the
 * transition and re-send its login-link email).
 */
export function useResumePaidSession(): ResumePaidSession {
  const dispatch = useAppDispatch();
  const sessionId = useAppSelector((s) => s.session.sessionId);
  const sessionLoaded = useAppSelector((s) => s.session.loaded);
  const sessionLoading = useAppSelector((s) => s.session.loading);
  const sessionError = useAppSelector((s) => s.session.error);
  const invoiceStatus = useAppSelector((s) => s.session.invoiceStatus);
  const invoiceStatusSettled = useAppSelector((s) => s.session.invoiceStatusSettled);
  const acknowledgement = useAppSelector((s) => s.session.paymentAcknowledgement);
  const acknowledging = useAppSelector((s) => s.session.paymentAcknowledging);
  const needsAcknowledge = useAppSelector(selectNeedsPaymentAcknowledgement);
  const checkedOut = useAppSelector(selectSessionCheckedOut);
  const showConfirmation = useAppSelector(selectShowPaymentConfirmation);

  // Seed the invoice status. The `invoice_update` websocket only reports while a page is OPEN, so
  // a buyer returning in a fresh tab after their payment cleared has no live signal and this read
  // is the only way to learn it. Skipped once any status is known: a websocket message that
  // already arrived is fresher than a read.
  //
  // Both dispatches below look unguarded on purpose. A flow mounts this hook TWICE (root +
  // checkout step); the two re-render in the same commit and React drains every passive effect of
  // that commit before processing the updates those effects schedule, so NO check made here can
  // stop the sibling — it would read the same stale snapshot. The real guards are `condition`
  // options on both thunks, evaluated against the live store at dispatch time. That matters most
  // for the acknowledge, which is a state transition and not idempotent.
  React.useEffect(() => {
    // `sessionLoaded` is load-bearing, not decoration: `sessionId` is rehydrated synchronously from
    // localStorage, so without it the very first render probes a PERSISTED id that `sessionEnsure`
    // may be about to replace — and that read would then land on whichever session won.
    if (!sessionLoaded || !sessionId || invoiceStatus) return;
    void dispatch(sessionFetchInvoiceStatus({ sessionId }));
  }, [dispatch, sessionLoaded, sessionId, invoiceStatus]);

  // Paid but never advanced: tell the backend.
  React.useEffect(() => {
    if (!sessionId || !needsAcknowledge) return;
    void dispatch(sessionAcknowledgePayment({ sessionId }));
  }, [dispatch, sessionId, needsAcknowledge]);

  /**
   * Still resolving — i.e. `checkedOut: false` cannot yet be trusted.
   *
   * Written as an ordered set of questions rather than one big disjunction, because it MUST always
   * reach a decision: a checkout holds its payment pane closed on this, so a `resolving` that
   * cannot go false is a wizard the buyer can never pay from.
   *
   *  1. session neither loaded nor failed yet → wait (nothing to reason about).
   *  2. no session at all → nothing to probe, so not resolving; the checkout renders its own
   *     "no session" error as before.
   *  3. the invoice status is neither known nor settled → wait. `settled` (not `invoiceStatus`) is
   *     what closes this: a read that failed or timed out returns no status, and treating that as
   *     "keep waiting" would wedge the gate. The probe carries a timeout for exactly this reason.
   *  4. otherwise resolving only while the acknowledge is pending or still owed.
   */
  // A failed `/session/load` never sets `loaded`, so "has it loaded yet" alone can never go false
  // for it. Treat a settled error as settled: otherwise a network blip on boot holds the checkout
  // closed forever instead of letting it render its own retry.
  const loadSettled = sessionLoaded || (sessionError != null && !sessionLoading);

  const resolving = !loadSettled
    ? true
    : sessionId == null
      ? false
      : invoiceStatus == null && !invoiceStatusSettled
        ? true
        : acknowledging || needsAcknowledge;

  return { acknowledgement, checkedOut, showConfirmation, resolving };
}

export default useResumePaidSession;
